<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';

import { AppSvgIcon } from '@libs/ui';
import type { Coordinate } from '@libs/trip-planner-interfaces';

import { useAutoCompleteStore } from './stores/autoCompleteStore';
import type { AutoCompleteType } from './AutoComplete.model';

interface AutoCompleteHeaderProps {
  autoCompleteType: AutoCompleteType;
  initialSearchText?: string;
  searchPosition?: Coordinate;
  autoFocus?: boolean;
}
const props = withDefaults(defineProps<AutoCompleteHeaderProps>(), {
  initialSearchText: '',
  showBackButton: true,
  autoFocus: true,
});

interface AutoCompleteHeaderEmits {
  (eventName: 'arrow-back-click'): void;
  (eventName: 'clear-search-text'): void;
}

const emit = defineEmits<AutoCompleteHeaderEmits>();

const input = ref<HTMLInputElement | null>(null);

const autoCompleteStore = useAutoCompleteStore();
autoCompleteStore.initializeAutoComplete(
  props.autoCompleteType,
  props.initialSearchText
);

function clearSearchText(): void {
  emit('clear-search-text');
  autoCompleteStore.cleanAutoCompleteSuggestionsAndSearchText();
}

watch(
  () => props.searchPosition,
  (position) => {
    autoCompleteStore.setSearchPosition(position);
  },
  { immediate: true }
);

watch(
  () => autoCompleteStore.searchText,
  (searchText) => {
    if (!searchText) {
      clearSearchText();
    }
  }
);

watch(
  () => props.autoFocus,
  (autoFocus) => {
    if (autoFocus) {
      nextTick(() => {
        input.value?.focus();
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <header class="auto-complete-header">
    <div class="input-container">
      <input
        ref="input"
        v-model="autoCompleteStore.searchText"
        :placeholder="autoCompleteStore.autoCompletePlaceholder"
        type="text"
        class="input"
        @input="
          autoCompleteStore.setSearchText(
            ($event.target as HTMLInputElement).value
          )
        "
      />
      <button class="circle-button" @click="clearSearchText">
        <AppSvgIcon name="close" size="9" />
      </button>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.auto-complete-header {
  --input-height: 56px;

  .input-container {
    position: relative;
    display: flex;
    height: var(--input-height);
    flex-grow: 1;

    .input {
      width: 100%;
      border: 2px solid transparent;
      border-radius: var(--border-radius-12);
      padding: 0 var(--spacing-40) 0 var(--spacing-16);
      background-color: var(--color-neutral-30);
      @include font-label-l-bold;
      color: var(--color-neutral-70);
      transition: all var(--transition-medium);
      @include on-hover {
        background-color: var(--color-neutral-30);
      }
      &:focus {
        background-color: var(--color-white);
        border: 2px solid var(--color-neutral-70);
      }
    }

    .close-button {
      position: absolute;
      right: var(--spacing-16);
      top: 50%;
      transform: translateY(-50%);
    }

    .circle-button {
      position: absolute;
      right: var(--spacing-16);
      top: 50%;
      transform: translateY(-50%);
      padding: var(--spacing-4);

      background-color: var(--color-neutral-70);
      border-radius: 50%;
      color: var(--color-white);
      @include on-hover {
        background: var(--color-neutral-70);
      }
    }
  }
}
</style>
